/**
 * Chapter 3, the audience: the overview (40), a persona (31), the people in
 * one table (06).
 */
import type { AudienceWho, StrategyPerson, StrategyPlatform } from "@/types/strategyDocument.type";
import {
  Eyebrow, Foot, Glyph, Headline, Key, Picture, Slide, SourceLine, V,
  countryName, Flag, joinAnd, platformName, rangeShort, slideText,
  type TemplateContext, NO_FIGURE
} from "./kit";

/** The headline picture: Instagram when connected, else Snapchat, YouTube, TikTok (schema 3.2). */
function headlineWho(who: AudienceWho[]): AudienceWho | undefined {
  const order: StrategyPlatform[] = ["INSTAGRAM", "SNAPCHAT", "YOUTUBE", "TIKTOK"];
  const connected = who.find((w) => w.platform === "INSTAGRAM" && w.access === "connected");
  return connected ?? order.map((p) => who.find((w) => w.platform === p)).find(Boolean) ?? who[0];
}

const hourLabel = (h: number) => (h === 0 ? "12 am" : h < 12 ? `${h} am` : h === 12 ? "12 pm" : `${h - 12} pm`);

/** 24 bars, the three busiest in blue. */
export function HourBars({ shares, height, barWidth, gap }: { shares: number[]; height: number; barWidth?: number; gap: number }) {
  const max = Math.max(...shares, 0.0001);
  const top = [...shares].map((s, h) => ({ s, h })).sort((a, b) => b.s - a.s).slice(0, 3).map((x) => x.h);
  return (
    <div className="sv-hours" style={{ height, gap }}>
      {shares.map((s, h) => (
        <i key={h} data-peak={top.includes(h) || undefined} style={{ height: Math.max(3, Math.round((s / max) * height)), ...(barWidth ? { width: barWidth } : {}) }} title={`${hourLabel(h)}: ${s}%`} />
      ))}
    </div>
  );
}

export function hourSeries(rows: Array<{ hour: number; share: number }>): number[] {
  const series = Array.from({ length: 24 }, () => 0);
  for (const r of rows) series[r.hour] = r.share;
  return series;
}

export const peakHour = (series: number[]) => series.indexOf(Math.max(...series));
export { hourLabel };

/* ── 40 The audience ──────────────────────────────────────────────────── */

export function AudienceSlide({ ctx }: { ctx: TemplateContext }) {
  const { doc } = ctx;
  const audience = doc.audience!;
  const t = slideText(ctx);
  const who = headlineWho(audience.who);
  const countries = [...(who?.countries ?? [])].sort((a, b) => b.share - a.share);
  const [first, second] = countries;
  const elsewhere = countries.length ? Math.max(0, 100 - countries.slice(0, 2).reduce((n, c) => n + c.share, 0)) : 0;
  const maxAge = Math.max(...(who?.age_bands ?? []).map((a) => a.share), 1);
  const markets = (doc.brief?.markets ?? []).map((m) => m.country);
  const [big, small] = markets.length ? markets : countries.map((c) => c.country);
  const reach = audience.behaviour?.platforms_by_country ?? [];
  const platforms = [...new Set(reach.map((r) => r.platform))]
    .map((p) => ({ p, big: reach.find((r) => r.platform === p && r.country === big)?.reach_share, small: reach.find((r) => r.platform === p && r.country === small)?.reach_share }))
    .sort((a, b) => (b.big ?? 0) - (a.big ?? 0))
    .slice(0, 4);
  const online = (audience.behaviour?.online_times ?? []).filter((r) => !r.market);
  const allDay = hourSeries(online.filter((r) => r.day === "all"));
  const friday = online.filter((r) => r.day === "friday");
  const hasHours = online.some((r) => r.day === "all");
  const peak = peakHour(allDay);
  const fridayPeak = friday.length ? [...friday].sort((a, b) => b.share - a.share)[0].hour : null;
  const questions = [...audience.questions].sort((a, b) => b.count - a.count);
  const [top, ...rest] = questions;
  const sample = audience.comments_sample;
  const days = sample?.window_start && sample.window_end ? Math.round((Date.parse(sample.window_end) - Date.parse(sample.window_start)) / 86_400_000) + 1 : null;
  const everyN = top && sample?.comments ? Math.round(sample.comments / top.count) : null;
  const EVERY = ["", "", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];
  return (
    <Slide>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} />
        <Headline text={t.headline} fallback="Who they are, *where and when.*" />
        <div className="sv-aud">
          <div className="sv-aud-col">
            <Key>Where they live · share of the audience</Key>
            {first ? (
              <div className="sv-treemap">
                <div className="sv-tm-first">
                  <p className="sv-tm-name"><Flag code={first.country} size={13} /><V>{countryName(first.country).replace(/^the /, "")}</V></p>
                  <V as="p" className="sv-tm-share">{first.share}%</V>
                </div>
                <div className="sv-tm-rest">
                  {second ? (
                    <div className="sv-tm-second">
                      <p className="sv-tm-name"><Flag code={second.country} size={11} /><V>{countryName(second.country).replace(/^the /, "")}</V></p>
                      <V as="p" className="sv-tm-share">{second.share}%</V>
                    </div>
                  ) : null}
                  <div className="sv-tm-else"><V as="b">{elsewhere}%</V><span>elsewhere</span></div>
                </div>
              </div>
            ) : <p className="sv-open">Open: the audience’s countries are not read yet.</p>}
            <SourceLine source={who?.source} className="sv-source-plain">{who?.source?.label?.split(" · ")[0]}</SourceLine>
            <div className="sv-aud-who">
              {who?.gender ? (
                <div className="sv-gender">
                  <Key>Women · men</Key>
                  <div className="sv-gender-donut">
                    <svg viewBox="0 0 96 96" width="96" height="96" aria-hidden>
                      <circle cx="48" cy="48" r="38" fill="none" stroke="#d9d9d9" strokeWidth="20" />
                      <circle cx="48" cy="48" r="38" fill="none" stroke="#207cf5" strokeWidth="20" strokeDasharray={`${(who.gender.women / 100) * 2 * Math.PI * 38} ${2 * Math.PI * 38}`} transform="rotate(-90 48 48)" />
                    </svg>
                    <div><span>♀</span><V as="b">{who.gender.women}%</V></div>
                  </div>
                  <p className="sv-gender-label"><V>{who.gender.women}% women · {who.gender.men}% men</V></p>
                </div>
              ) : null}
              {who?.age_bands.length ? (
                <div className="sv-age">
                  <Key>Age</Key>
                  <div className="sv-age-bars">
                    {who.age_bands.map((a) => (
                      <div key={a.band} className="sv-age-band">
                        <V as="b">{a.share}%</V>
                        <i data-top={a.share === maxAge || undefined} style={{ height: Math.round((a.share / maxAge) * 60) }} />
                        <V>{a.band}</V>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
            {who && (who.languages.length || who.cities.length) ? (
              <p className="sv-aud-lang">
                {who.languages.length ? <>Language: <V>{who.languages.map((l) => `${l.language} ${l.share}%`).join(", ")}</V>. </> : null}
                {who.cities.length ? <>Cities: <V>{who.cities.join(", ")}</V>.</> : null}
              </p>
            ) : null}
            <SourceLine source={who?.source} className="sv-source-plain" />
          </div>

          <div className="sv-aud-col">
            <Key>Reach of internet users{big ? <> · <Flag code={big} size={10} /> big{small ? <>, <Flag code={small} size={10} /> small</> : null}</> : null}</Key>
            {platforms.length ? (
              <div className="sv-reach">
                {platforms.map((p) => (
                  <div key={p.p} className="sv-reach-card">
                    <p className="sv-reach-name"><Glyph platform={p.p} size={18} />{platformName(p.p)}</p>
                    <V as="p" className="sv-reach-big">{p.big != null ? `${p.big}%` : NO_FIGURE}</V>
                    {small && p.small != null ? <p className="sv-reach-small"><Flag code={small} size={10} /> <V>{p.small}%</V></p> : null}
                  </div>
                ))}
              </div>
            ) : <p className="sv-open">Open: the reference table per country is not written yet.</p>}
            <SourceLine source={reach[0]?.source} className="sv-source-plain">{reach[0]?.source?.label ?? (reach.length ? "Reference table per country" : null)}</SourceLine>
            <Key className="sv-aud-gap">When they are online · share of the day’s activity</Key>
            {hasHours ? (
              <>
                <HourBars shares={allDay} height={56} gap={2} />
                <div className="sv-hour-axis"><span>12 am</span><span>6 am</span><span>12 pm</span><span>6 pm</span><span>11 pm</span></div>
                <p className="sv-peak"><V>Peak {hourLabel(peak)}.</V>{fridayPeak != null ? <> <V>Friday peaks at {hourLabel(fridayPeak)}.</V></> : null}{audience.behaviour?.online_note ? <> <V>{audience.behaviour.online_note}</V></> : null}</p>
                <SourceLine source={audience.behaviour?.online_source} className="sv-source-plain" />
              </>
            ) : <p className="sv-open sv-aud-open">Open: online times come from a connected Instagram.</p>}
          </div>

          <div className="sv-aud-col">
            <Key>What they ask in the comments</Key>
            {top ? (
              <div className="sv-top-question">
                <V as="p" className="sv-top-count">{top.count.toLocaleString("en-US")}</V>
                <V as="p" className="sv-top-text">“{top.question.replace(/^[“"]|[”"]$/g, "")}”</V>
                <p className="sv-top-note">times, under the set’s posts{days ? <> in <V>{days > 85 && days < 95 ? 90 : days}</V> days</> : null}.{everyN && everyN > 1 && everyN <= 10 ? <> One comment in every <V>{EVERY[everyN]}</V>.</> : null}</p>
              </div>
            ) : null}
            {rest.slice(0, 4).map((q) => (
              <div key={q.id} className="sv-question">
                <V as="b">{q.count.toLocaleString("en-US")}</V>
                <V as="p">“{q.question.replace(/^[“"]|[”"]$/g, "")}”</V>
              </div>
            ))}
            {sample ? <SourceLine className="sv-source-plain">Comments on the set’s posts, {rangeShort(sample.window_start, sample.window_end)}, {sample.comments.toLocaleString("en-US")} read</SourceLine> : null}
          </div>
        </div>
        <Foot ctx={ctx} />
      </div>
    </Slide>
  );
}

/* ── 31 A persona ─────────────────────────────────────────────────────── */

const pronouns = (p: StrategyPerson) => (p.pronoun === "he" ? { is: "he is", her: "his", she: "he" } : p.pronoun === "they" ? { is: "they are", her: "their", she: "they" } : { is: "she is", her: "her", she: "she" });
const BLOCKS = [
  { key: "Wants", colour: "#207cf5", ink: "#ffffff" },
  { key: "Needs", colour: "#04e735", ink: "#121212" },
  { key: "Triggers", colour: "#6a00ba", ink: "#ffffff" },
  { key: "Avoids", colour: "#f66666", ink: "#ffffff" },
];

export function PersonaSlide({ ctx }: { ctx: TemplateContext }) {
  const audience = ctx.doc.audience!;
  const tpl = ctx.slide.template.id === "persona" ? ctx.slide.template : null;
  const person = audience.people[tpl?.person ?? 0];
  const t = slideText(ctx);
  const words = pronouns(person);
  const situations = person.situation_ids.map((id) => audience.situations.find((s) => s.id === id)?.situation).filter((s): s is string => !!s);
  const meta = [person.age, person.city, person.language, person.household, person.work].filter((v) => v != null && v !== "");
  const lists = [person.seeks, person.needs, situations, person.avoids];
  const quote = person.evidence.find((e) => e.excerpt) ?? person.evidence[0];
  const title = t.headline ?? `${person.name}${person.age ? `, ${person.age}` : ""}${person.city ? `, ${person.city}` : ""}.${person.one_line ? ` *${person.one_line}*` : ""}`;
  return (
    <Slide>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} extra={<>Persona {(tpl?.person ?? 0) + 1} of {tpl?.of ?? audience.people.length}</>} />
        <Headline text={title} />
        <div className="sv-persona">
          <Picture ctx={ctx} slot={`person:${person.id}`} label={`${person.name}’s picture`} className="sv-persona-pic" />
          <div className="sv-persona-profile">
            <V as="p" className="sv-persona-name">{person.name}</V>
            {meta.length ? <V as="p" className="sv-persona-meta">{meta.join(" · ")}</V> : null}
            {person.core_motivation ? <V as="p" className="sv-persona-motivation">{person.core_motivation}</V> : null}
            <div className="sv-persona-blocks">
              {BLOCKS.map((b, i) => (
                <div key={b.key} className="sv-persona-block">
                  <span className="sv-persona-key" style={{ background: b.colour, color: b.ink }}>{b.key}</span>
                  {lists[i].map((line, j) => <V key={j} as="p" className={j === 0 ? "sv-persona-first" : undefined}>{line}</V>)}
                </div>
              ))}
            </div>
            <div className="sv-persona-evidence">
              <div>
                <Key>Where {words.she} is</Key>
                {person.digital_home.map((d, i) => (
                  <p key={i} className="sv-persona-home"><Glyph platform={d.platform} size={16} /><V>{d.line}</V></p>
                ))}
              </div>
              {quote ? (
                <div>
                  <Key>In {words.her} words</Key>
                  <V as="p" className="sv-persona-quote" dir="auto">“{(quote.excerpt ?? "").replace(/^[“"]|[”"]$/g, "")}”</V>
                  <p className="sv-persona-source">
                    {quote.translation ? <V>“{quote.translation}” </V> : null}
                    {quote.note ? <V>{quote.note} </V> : null}
                    {quote.url ? <V>{quote.url.replace(/^https?:\/\/(www\.)?/, "")}…</V> : null}
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <Foot ctx={ctx} label={`${ctx.chapter?.foot ?? ""} · ${person.name}`} />
      </div>
    </Slide>
  );
}

/* ── 06 The people, one table ─────────────────────────────────────────── */

const TRIGGER_COLOURS = ["#207cf5", "#04e735", "#6a00ba", "#f66666", "#121212"];

export function PeopleSlide({ ctx }: { ctx: TemplateContext }) {
  const audience = ctx.doc.audience!;
  const indexes = ctx.slide.template.id === "people" ? ctx.slide.template.people : [];
  const t = slideText(ctx);
  const n = audience.people.length;
  return (
    <Slide>
      <div className="sv-col sv-fill">
        <Eyebrow ctx={ctx} extra="Recap" />
        <Headline text={t.headline} fallback={`${n === 1 ? "One person" : `${["", "One", "Two", "Three", "Four", "Five"][n] ?? n} people`}, *one table.*`} />
        <div className="sv-people">
          {indexes.map((i) => {
            const p = audience.people[i];
            const trigger = audience.situations.find((s) => s.id === p.situation_ids[0]);
            return (
              <div key={p.id} className="sv-people-card">
                <Picture ctx={ctx} slot={`person:${p.id}`} label={`${p.name}’s picture`} className="sv-people-pic" />
                <V as="p" className="sv-people-name">{[p.name, p.age, p.city].filter((v) => v != null && v !== "").join(", ")}</V>
                {p.one_line ? <V as="p" className="sv-people-line">{p.one_line}</V> : null}
                <div className="sv-people-pills">
                  {trigger ? <span className="sv-tag" style={{ background: TRIGGER_COLOURS[i % TRIGGER_COLOURS.length], color: "#ffffff" }}>Trigger · <V>{(trigger.short ?? trigger.situation).replace(/\.$/, "")}</V></span> : null}
                  {p.digital_home[0] ? <span className="sv-tag sv-tag-soft"><V>{platformName(p.digital_home[0].platform)}</V> first</span> : null}
                </div>
              </div>
            );
          })}
        </div>
        {audience.shared ? (
          <div className="sv-closing sv-closing-15">
            <Key>What they share</Key>
            <V as="p">{audience.shared}</V>
          </div>
        ) : null}
        <Foot ctx={ctx} />
      </div>
    </Slide>
  );
}

export const listPlatforms = (ps: StrategyPlatform[]) => joinAnd(ps.map(platformName));
